// Packs are read from versioned directories, `packs/<id>/<version>/`, which the public site serves immutable (the lead's
// layout, 2026-10-03), so a repeat visit no longer revalidates every pack file: that was about 1,000 304s and 1.4–2.4 s
// of a warm load (research/2026-10-03-warm-cache.md). `packs/index.json` stays revalidated and names each pack's
// current version.
//
// The engine builds `<assets>packs/<id>/<file>` itself, for a pack and the packs it extends, and every read it makes
// goes through the page's fetch (its only worker is winit's scheduler). So the page maps the reads rather than each
// reader: installPackPaths() wraps fetch once, and a read of `packs/<id>/<file>` goes to `packs/<id>/<version>/<file>`,
// the engine's, the catalogue's, the sims readers' and a feature's alike. A pack the index doesn't list (or a server with
// no index) keeps the bare directory, unless the page added its version (addPackVersions: a lab's own packs). A read the network fails, the index's or its own, goes again once a moment later,
// before its reader hears of it, as does a sims read; if that fails too, so does the read, never falling back to the
// bare directory, which goes from the public site with the old paths. The local server (server/static.ts) serves a listed pack only under its
// current version, so a read that misses this mapping fails the gates instead of the public site after the old paths go.

import { networkFailed, RETRY_MS, sleep } from './retry.ts';

/** Pack id → the version its files are served under. */
export type PackVersions = ReadonlyMap<string, string>;

/** One path segment: what an id must be to name a directory. */
const segment = (s: unknown): s is string => typeof s === 'string' && s !== '' && s !== '.' && s !== '..' && !/[/\\?#%]/.test(s);
/** A version the publisher puts in a path (scripts/publish-public.py PACK_VERSION). */
const VERSION = /^[0-9A-Za-z][0-9A-Za-z._+-]{0,63}$/;

/** The versions `packs/index.json` lists (an array of rows, or `{ packs: [...] }`). Rows without a usable id and
 *  version are left out, and their packs keep the bare directory. */
export function packVersionsOf(index: unknown): PackVersions {
  const rows = Array.isArray(index) ? index : ((index as { packs?: unknown } | null)?.packs ?? []);
  const out = new Map<string, string>();
  for (const r of Array.isArray(rows) ? rows : []) {
    const { id, version } = (r ?? {}) as { id?: unknown; version?: unknown };
    if (segment(id) && typeof version === 'string' && VERSION.test(version)) out.set(id, version);
  }
  return out;
}

const read = new WeakMap<typeof fetch, Map<string, Promise<PackVersions>>>();
const NONE: PackVersions = new Map();

/** `packs/index.json`'s versions under `assets`, read once per fetch. A server with no usable index (a 4xx, or JSON this
 *  can't read) has no versions; an index the network failed (a TypeError) or the server did (a 5xx) rejects. Neither is
 *  kept: the next read asks again. */
export function packVersions(assets = '/', fetchFn: typeof fetch = fetch): Promise<PackVersions> {
  let byAssets = read.get(fetchFn);
  if (!byAssets) read.set(fetchFn, (byAssets = new Map()));
  let p = byAssets.get(assets);
  if (!p) {
    const mine = byAssets;
    const asked: Promise<PackVersions> = fetchFn(`${assets}packs/index.json`).then((res) => {
      if (res.status >= 500) throw new Error(`packs/index.json: HTTP ${res.status}`);
      if (!res.ok) return NONE;
      // A body the network cut off is the network's failure; a body that isn't JSON is no index.
      return res.json().then(packVersionsOf, (err: unknown) => (err instanceof TypeError ? Promise.reject(err) : NONE));
    });
    p = asked;
    const forget = () => mine.get(assets) === asked && mine.delete(assets);
    mine.set(assets, asked);
    asked.then((v) => v === NONE && forget(), forget);
  }
  return p;
}

/** A pack's directory: `<assets>packs/<id>/<version>/` for a pack the index lists, else `<assets>packs/<id>/`. */
export function packDir(id: string, versions: PackVersions, assets = '/'): string {
  const v = versions.get(id);
  return v ? `${assets}packs/${id}/${v}/` : `${assets}packs/${id}/`;
}

/**
 * The versioned URL for a read of a listed pack's `packs/<id>/<file>` under `packsUrl` (the absolute URL of
 * `<assets>packs/`), keeping its query; null for any other URL: another origin or prefix, `packs/index.json`, an
 * unlisted pack, or a read that already names the version.
 */
export function versionedPackUrl(url: string, packsUrl: string, versions: PackVersions): string | null {
  let u: URL;
  try {
    u = new URL(url, packsUrl);
  } catch {
    return null;
  }
  const base = new URL(packsUrl);
  if (u.origin !== base.origin || !u.pathname.startsWith(base.pathname)) return null;
  const rest = u.pathname.slice(base.pathname.length);
  const cut = rest.indexOf('/');
  if (cut <= 0) return null;
  const id = rest.slice(0, cut);
  const v = versions.get(id);
  if (!v || rest.startsWith(`${id}/${v}/`)) return null;
  u.pathname = `${base.pathname}${id}/${v}/${rest.slice(cut + 1)}`;
  return u.href;
}

/** Versions a page adds for packs `packs/index.json` doesn't list, with how many callers added each. */
const added = new Map<string, { version: string; n: number }>();

/**
 * Send this page's reads of these packs to `packs/<id>/<version>/` too, for packs the index doesn't list: a lab's own
 * packs, which stay out of the shared index until the lab graduates (the lead's D2 for the Margin lab), named by the
 * lab's own index. An id the index lists keeps the index's version. Adding an id again with another version throws.
 * Returns the undo, which takes back what this call added once no other caller still holds it.
 */
export function addPackVersions(rows: Iterable<readonly [string, string]>): () => void {
  const list = [...rows];
  for (const [id, version] of list) {
    if (!segment(id) || !VERSION.test(version)) throw new Error(`addPackVersions: ${JSON.stringify(id)} at ${JSON.stringify(version)} isn't a pack id and a version`);
    const had = added.get(id);
    if (had && had.version !== version) throw new Error(`addPackVersions: ${id} is already at ${had.version}, not ${version}`);
  }
  for (const [id, version] of list) {
    const had = added.get(id);
    if (had) had.n++;
    else added.set(id, { version, n: 1 });
  }
  let done = false;
  return () => {
    if (done) return;
    done = true;
    for (const [id] of list) {
      const had = added.get(id);
      if (had && --had.n <= 0) added.delete(id);
    }
  };
}

/** The index's versions with the added ones under them: the index wins for an id it lists. */
export function withAddedVersions(versions: PackVersions): PackVersions {
  if (!added.size) return versions;
  const out = new Map<string, string>();
  for (const [id, a] of added) out.set(id, a.version);
  for (const [id, v] of versions) out.set(id, v);
  return out;
}

let installed: (() => void) | null = null;

/** Send this page's pack reads to their versioned directories from now on (the index's, and the ones a page added
 *  with addPackVersions), and read again once a pack or sims read the network failed. Idempotent; returns the undo. */
export function installPackPaths(assets = '/'): () => void {
  if (installed) return installed;
  if (typeof globalThis.fetch !== 'function' || typeof location === 'undefined') return () => {};
  const original = globalThis.fetch;
  const raw: typeof fetch = (...a) => original.apply(globalThis, a);
  const packsUrl = new URL(`${assets}packs/`, location.href).href;
  const simsUrl = new URL(`${assets}sims/`, location.href).href;
  const mapped: typeof fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const href = URL.canParse(url, location.href) ? new URL(url, location.href).href : '';
    const pack = href.startsWith(packsUrl);
    if (!pack && !href.startsWith(simsUrl)) return raw(input, init);
    // A read the network failed goes again once, a moment later (data/retry.ts), the index's and the file's each: on a
    // network change every read in flight fails together, and they go again together, before any reader hears of it.
    // The engine starts its requests before it awaits them, so one that failed would reach the page as uncaught.
    const signal = init?.signal ?? (input instanceof Request ? input.signal : null);
    const again = <T>(f: () => Promise<T>): Promise<T> =>
      f().catch(async (err: unknown) => {
        if (!networkFailed(err) || signal?.aborted) throw err;
        await sleep(RETRY_MS);
        if (signal?.aborted) throw err;
        return f();
      });
    // A sim's files, the engine's frames and the app's headers alike, keep their paths.
    if (!pack) return again(() => raw(input, init));
    // Only a pack read waits for the index.
    return again(() => packVersions(assets, raw)).then(
      (versions) => {
        const to = versionedPackUrl(url, packsUrl, withAddedVersions(versions));
        // A Request (the engine's ranged reads) keeps its method, headers and signal.
        return again(() => (to ? raw(input instanceof Request ? new Request(to, input) : to, init) : raw(input, init)));
      },
      // An index the network failed twice fails the read with the network's error, and one the server failed answers
      // it 503, which its reader takes as it would its own 5xx; the next read asks for the index again.
      (err: unknown) => (err instanceof TypeError ? Promise.reject(err) : new Response(null, { status: 503, statusText: 'packs/index.json unreadable' })),
    );
  };
  globalThis.fetch = mapped;
  const undo = () => {
    if (globalThis.fetch === mapped) globalThis.fetch = original;
    installed = null;
  };
  installed = undo;
  return undo;
}
